import React from 'react';

const STAR_FILL = 'var(--voice-widget-star, #F59E0B)';

/** Full star (Font Awesome solid star). */
function StarFull({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 640 640"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path
        fill={STAR_FILL}
        d="M341.5 45.1C337.4 37.1 329.1 32 320.1 32C311.1 32 302.8 37.1 298.7 45.1L225.1 189.3L65.2 214.7C56.3 216.1 48.9 222.4 46.1 231C43.3 239.6 45.6 249 51.9 255.4L166.3 369.9L141.1 529.8C139.7 538.7 143.4 547.7 150.7 553C158 558.3 167.6 559.1 175.7 555L320.1 481.6L464.4 555C472.4 559.1 482.1 558.3 489.4 553C496.7 547.7 500.4 538.8 499 529.8L473.7 369.9L588.1 255.4C594.5 249 596.7 239.6 593.9 231C591.1 222.4 583.8 216.1 574.8 214.7L415 189.3L341.5 45.1z"
      />
    </svg>
  );
}

/** Half star (Font Awesome solid star-half). */
function StarHalf({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 576 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path
        fill={STAR_FILL}
        d="M304.1 7.6c0-11.1-7.6-20.7-18.4-23.3s-21.9 2.5-27 12.4L193.1 125.3 33.2 150.7c-8.9 1.4-16.3 7.7-19.1 16.3s-.5 18 5.8 24.4l114.4 114.5-25.2 159.9c-1.4 8.9 2.3 17.9 9.6 23.2s16.9 6.1 25 2L291 416.1c8-4.1 13.1-12.4 13.1-21.4l0-387.1z"
      />
    </svg>
  );
}

/** Outlined empty star (Font Awesome regular star). */
function StarEmpty({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 576 512"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      style={{ display: 'block', flexShrink: 0 }}
    >
      <path
        fill={STAR_FILL}
        d="M287.9 0C297.1 0 305.5 5.25 309.5 13.52L378.1 154.8L531.4 177.5C540.4 178.8 547.8 185.1 550.7 193.7C553.5 202.4 551.2 211.9 544.8 218.2L433.6 328.4L459.9 483.9C461.4 492.9 457.7 502.1 450.2 507.4C442.8 512.7 432.1 513.4 424.9 509.1L287.9 435.9L150.1 509.1C142.9 513.4 133.1 512.7 125.6 507.4C118.2 502.1 114.5 492.9 116 483.9L142.3 328.4L31.11 218.2C24.65 211.9 22.36 202.4 25.32 193.7C28.27 185.1 35.66 178.8 44.61 177.5L197.9 154.8L266.5 13.52C270.5 5.25 278.9 0 287.9 0L287.9 0zM287.9 78.95L235.4 187.2C231.9 194.3 225.1 199.3 217.3 200.5L98.98 217.9L184.9 303C190.4 308.5 192.9 316.4 191.6 324.1L171.4 443.7L276.6 387.5C283.7 383.7 292.2 383.7 299.2 387.5L404.4 443.7L384.2 324.1C382.9 316.4 385.5 308.5 391 303L476.9 217.9L358.6 200.5C350.7 199.3 343.9 194.3 340.5 187.2L287.9 78.95z"
      />
    </svg>
  );
}

export interface AmazonStarRatingProps {
  amazonRating?: number;
  amazonReviewCount?: number;
  /** Icon size in px (default 14). */
  size?: number;
  /** Muted label font size (default 11). */
  countFontSize?: number;
  /**
   * - `short` (default): `★★★★☆ (1,423)`
   * - `long`: `★★★★☆ 3.7 (1,423 reviews)` — used by wide PDP widget.
   */
  format?: 'short' | 'long';
}

/**
 * Amazon-only rating row for voice product widgets.
 * Always renders 5 star slots (filled / half / empty SVGs). Review count is
 * shown when present.
 */
export function AmazonStarRating({
  amazonRating,
  amazonReviewCount,
  size = 14,
  countFontSize = 11,
  format = 'short',
}: AmazonStarRatingProps) {
  if (typeof amazonRating !== 'number' || amazonRating <= 0) return null;

  const full = Math.min(5, Math.floor(amazonRating));
  const hasHalf = amazonRating - full >= 0.5 && full < 5;
  const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

  const hasCount = typeof amazonReviewCount === 'number' && amazonReviewCount > 0;
  const countLabel = hasCount ? (amazonReviewCount as number).toLocaleString('en-US') : null;

  const text =
    format === 'long'
      ? `${amazonRating.toFixed(1)}${countLabel ? ` (${countLabel} review${(amazonReviewCount as number) === 1 ? '' : 's'})` : ''}`
      : countLabel
        ? `(${countLabel})`
        : null;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 2 }}
        role="img"
        aria-label={`${amazonRating} out of 5 stars`}
      >
        {Array.from({ length: full }, (_, i) => (
          <StarFull key={`f-${i}`} size={size} />
        ))}
        {hasHalf ? <StarHalf size={size} /> : null}
        {Array.from({ length: empty }, (_, i) => (
          <StarEmpty key={`e-${i}`} size={size} />
        ))}
      </div>
      {text ? (
        <span style={{ fontSize: countFontSize, color: 'var(--voice-widget-muted, #6B7280)' }}>{text}</span>
      ) : null}
    </div>
  );
}
